// ===============================================================================
// GroupDetail.js - Group Detail Page for WOW
// ===============================================================================
// Location: /var/www/wow/frontend/src/GroupDetail.js
// Shows group expenses, balances, and settlement options
// UPDATED: Multiple payers, split options panel, expense details, smaller fonts
// ===============================================================================

import React, { useState, useEffect, useCallback } from "react";
//import { groupsAPI, expensesAPI, balancesAPI, settlementsAPI, exportAPI, formatCurrency, formatDate } from "./api";
import { groupsAPI, expensesAPI, balancesAPI, settlementsAPI, exportAPI, receiptsAPI, formatCurrency, formatDate } from "./api";
import "./GroupDetail.css";
import { simplifyDebts } from './balanceUtils';

function GroupDetail({ groupId, user, onBack, onGroupDeleted }) {
  // ===============================================================================
  // State
  // ===============================================================================
  const [group, setGroup] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [balances, setBalances] = useState(null);
  const [activeTab, setActiveTab] = useState("balances");
  const [loading, setLoading] = useState(true);
  const [expensesLoaded, setExpensesLoaded] = useState(false);
  const [loadingExpenses, setLoadingExpenses] = useState(false);
  const [hasMoreExpenses, setHasMoreExpenses] = useState(true);
  const [error, setError] = useState("");

  // Add Expense Form
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseCategory, setExpenseCategory] = useState("");
  const [addingExpense, setAddingExpense] = useState(false);
  
  // Multiple Payers
  const [multiplePayers, setMultiplePayers] = useState(false);
  const [payerAmounts, setPayerAmounts] = useState({});
  
  // Split Options
  const [splitType, setSplitType] = useState("percentage"); // percentage, equal, shares
  const [splitAmounts, setSplitAmounts] = useState({});
  const [memberShares, setMemberShares] = useState({});
  
// Settle Up Form
  const [settlements, setSettlements] = useState([]);
  const [settlementsLoaded, setSettlementsLoaded] = useState(false);
  const [loadingSettlements, setLoadingSettlements] = useState(false);
  const [showSettleUp, setShowSettleUp] = useState(false);
  const [settleTo, setSettleTo] = useState("");
  const [settleAmount, setSettleAmount] = useState("");
  const [settleNote, setSettleNote] = useState("");
  const [settling, setSettling] = useState(false);

  // Show Invite Code
  const [showInvite, setShowInvite] = useState(false);

  // Expense Detail Modal
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [loadingExpenseDetail, setLoadingExpenseDetail] = useState(false);

 // Receipt states
  const [receiptFile, setReceiptFile] = useState(null);
  const [uploadingReceipt, setUploadingReceipt] = useState(false);
  const [receiptInfo, setReceiptInfo] = useState(null);
  const [loadingReceipt, setLoadingReceipt] = useState(false);

  // Split Percentages State
  const [editingSplits, setEditingSplits] = useState(false);
  const [splitPercentages, setSplitPercentages] = useState({});
  const [savingSplits, setSavingSplits] = useState(false);
  const [splitError, setSplitError] = useState("");
  const [splitSuccess, setSplitSuccess] = useState("");
  
  // Default Shares State
  const [editingShares, setEditingShares] = useState(false);
  const [defaultShares, setDefaultShares] = useState({});
  const [savingShares, setSavingShares] = useState(false);
  const [sharesError, setSharesError] = useState("");
  const [sharesSuccess, setSharesSuccess] = useState("");

  // ===============================================================================
  // Categories
  // ===============================================================================
  const categories = [
    { value: "", label: "No Category" },
    { value: "accommodation", label: "🏠 Accommodation" },
    { value: "finance", label: "💳 Banking & Finance" },
    { value: "business", label: "💼 Business Expenses" },
    { value: "automotive", label: "🔧 Car Maintenance" },
    { value: "cleaning", label: "🧺 Cleaning & Laundry" },
    { value: "education", label: "🎓 Education" },
    { value: "entertainment", label: "🎬 Entertainment" },
    { value: "food", label: "🍕 Food & Drinks" },
    { value: "fuel", label: "⛽ Fuel & Petrol" },
    { value: "garden", label: "🌳 Garden" },
    { value: "gifts", label: "🎁 Gifts & Presents" },
    { value: "groceries", label: "🛒 Groceries" },
    { value: "health", label: "💊 Health" },
    { value: "houseexpense", label: "🏠 House Expenses" },
    { value: "nightlife", label: "🎶 NightLife" },
    { value: "personalcare", label: "✂️ Personal Care" },
    { value: "pets", label: "🐶 Pet Expenses" },
    { value: "shopping", label: "👗 Shopping" },
    { value: "supplements", label: "🌿 Supplements" },
    { value: "subscriptions", label: "📱 Subscriptions" },
    { value: "taxes", label: "🧾 Taxes" },
    { value: "telecom", label: "📶 Telecommunication" },
    { value: "transport", label: "🚗 Transport" },
    { value: "travels", label: "✈️ Travels & Vacations" },
    { value: "utilities", label: "💡 Utilities" },
    { value: "other", label: "📦 Other" },
  ];

  // ===============================================================================
  // Load Data
  // ===============================================================================
  const loadGroupData = useCallback(async () => {
    console.log("🔄 loadGroupData called at:", new Date().toISOString());
    setLoading(true);
    setError("");
    try {
      const [groupRes, balancesRes, settlementsRes] = await Promise.all([
        groupsAPI.get(groupId),
        balancesAPI.get(groupId),
      ]);
      
      if (groupRes.data) {
        setGroup(groupRes.data);
        // Initialize split percentages and shares from members
        const initialSplits = {};
        const initialShares = {};
        const initialPayerAmounts = {};
        const initialSplitAmounts = {};
        const initialMemberShares = {};
        
        groupRes.data.members?.forEach(m => {
          initialSplits[m.user_id] = m.split_percentage || 0;
          initialShares[m.user_id] = m.default_shares || 1;
          initialPayerAmounts[m.user_id] = 0;
          initialSplitAmounts[m.user_id] = 0;
          initialMemberShares[m.user_id] = m.default_shares || 1;
        });
        setSplitPercentages(initialSplits);
        setDefaultShares(initialShares);
        setPayerAmounts(initialPayerAmounts);
        setSplitAmounts(initialSplitAmounts);
        setMemberShares(initialMemberShares);
      }
      if (balancesRes.data?.ok) {
        const balanceData = balancesRes.data;
      // Calculate debts in frontend if server didn't include them
      if (!balanceData.debts || balanceData.debts.length === 0) {
        balanceData.debts = simplifyDebts(balanceData.balances);
      }
      setBalances(balanceData);
    }
      console.log("✅ Initial load complete (group + balances only)");
    } catch (err) {
      console.error("Load group error:", err);
      setError("Failed to load group data");
    }
    setLoading(false);
  }, [groupId]);

  useEffect(() => {
    if (groupId) {
      loadGroupData();
    }

}, [groupId]);

  // ===============================================================================
  // LAZY LOAD: Settlements (called when user clicks "Load Payment History")
  // ===============================================================================
  const loadSettlements = useCallback(async () => {
    if (loadingSettlements || settlementsLoaded) return;
    
    console.log("🤝 Loading settlements...");
    setLoadingSettlements(true);
    try {
      const res = await settlementsAPI.list(groupId);
      if (res.data?.ok) {
        setSettlements(res.data.settlements || []);
        setSettlementsLoaded(true);
        console.log("✅ Settlements loaded:", res.data.settlements?.length || 0);
      }
    } catch (err) {
      console.error("Load settlements error:", err);
    }
    setLoadingSettlements(false);
  }, [groupId, loadingSettlements, settlementsLoaded]);

  // Bas- Added LoadExpenses fuction on demand //


const loadExpenses = useCallback(async (loadMore = false) => {
  if (loadingExpenses) return;
  
  setLoadingExpenses(true);
  try {
    const offset = loadMore ? expenses.length : 0;
    const res = await expensesAPI.list(groupId, 20, offset);
    
    if (res.data?.ok) {
      const newExpenses = res.data.expenses || [];
      
      if (loadMore) {
        // Append to existing
        setExpenses(prev => [...prev, ...newExpenses]);
      } else {
        // Replace (first load)
        setExpenses(newExpenses);
      }
      
      setExpensesLoaded(true);
      setHasMoreExpenses(newExpenses.length === 20); // If less than 20, no more
    }
  } catch (err) {
    console.error("Load expenses error:", err);
  }
  setLoadingExpenses(false);
}, [groupId, expenses.length, loadingExpenses]);

  // ===============================================================================
  // Initialize Split Amounts when Amount Changes
  // ===============================================================================
  useEffect(() => {
    if (expenseAmount && group?.members) {
      const amount = parseFloat(expenseAmount) || 0;
      recalculateSplits(amount, splitType);
    }
  }, [expenseAmount, group?.members]);

  const recalculateSplits = (amount, type) => {
    if (!group?.members) return;
    
    const newSplitAmounts = {};
    const newMemberShares = { ...memberShares };
    
    if (type === "percentage") {
      // Use default percentages
      group.members.forEach(m => {
        const pct = splitPercentages[m.user_id] || 0;
        newSplitAmounts[m.user_id] = Math.round((amount * pct / 100) * 100) / 100;
      });
    } else if (type === "equal") {
      // Equal split
      const perPerson = Math.round((amount / group.members.length) * 100) / 100;
      let remaining = amount;
      group.members.forEach((m, i) => {
        if (i === group.members.length - 1) {
          newSplitAmounts[m.user_id] = Math.round(remaining * 100) / 100;
        } else {
          newSplitAmounts[m.user_id] = perPerson;
          remaining -= perPerson;
        }
      });
    } else if (type === "shares") {
      // Shares-based split
      const totalShares = Object.values(newMemberShares).reduce((sum, s) => sum + (parseInt(s) || 0), 0);
      if (totalShares > 0) {
        const perShare = amount / totalShares;
        group.members.forEach(m => {
          const shares = parseInt(newMemberShares[m.user_id]) || 1;
          newSplitAmounts[m.user_id] = Math.round((perShare * shares) * 100) / 100;
        });
      }
    }
    
    setSplitAmounts(newSplitAmounts);
  };

  // ===============================================================================
  // Split Percentage Helpers
  // ===============================================================================
  const calculateSplitTotal = () => {
    return Object.values(splitPercentages).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const handleSplitChange = (userId, value) => {
    const numValue = value === "" ? "" : Math.max(0, Math.min(100, parseFloat(value) || 0));
    setSplitPercentages(prev => ({
      ...prev,
      [userId]: numValue
    }));
    setSplitError("");
    setSplitSuccess("");
  };

  const handleEqualSplit = () => {
    const memberCount = group?.members?.length || 1;
    const equalPct = Math.round((100 / memberCount) * 100) / 100;
    const newSplits = {};
    
    group?.members?.forEach((m, index) => {
      if (index === 0) {
        newSplits[m.user_id] = Math.round((100 - (equalPct * (memberCount - 1))) * 100) / 100;
      } else {
        newSplits[m.user_id] = equalPct;
      }
    });
    
    setSplitPercentages(newSplits);
    setSplitError("");
    setSplitSuccess("");
  };

  const handleSaveSplits = async () => {
    const total = calculateSplitTotal();
    
    if (Math.abs(total - 100) > 0.01) {
      setSplitError(`Percentages must sum to 100%. Current total: ${total.toFixed(2)}%`);
      return;
    }
    
    for (const val of Object.values(splitPercentages)) {
      if (parseFloat(val) < 0) {
        setSplitError("Percentages cannot be negative");
        return;
      }
    }
    
    setSavingSplits(true);
    setSplitError("");
    setSplitSuccess("");
    
    try {
      const percentagesArray = Object.entries(splitPercentages).map(([userId, percentage]) => ({
        user_id: parseInt(userId),
        percentage: parseFloat(percentage)
      }));
      
      const res = await groupsAPI.updateSplitPercentages(groupId, percentagesArray);
      
      if (res.data?.ok) {
        setSplitSuccess("Split percentages saved successfully!");
        setEditingSplits(false);
        loadGroupData();
      }
    } catch (err) {
      console.error("Save splits error:", err);
      setSplitError(err?.response?.data?.detail || "Failed to save split percentages");
    }
    
    setSavingSplits(false);
  };

  const handleResetSplits = async () => {
    if (!window.confirm("Reset to equal splits for all members?")) return;
    
    setSavingSplits(true);
    setSplitError("");
    setSplitSuccess("");
    
    try {
      const res = await groupsAPI.resetSplitPercentages(groupId);
      
      if (res.data?.ok) {
        setSplitSuccess("Reset to equal splits!");
        setEditingSplits(false);
        loadGroupData();
      }
    } catch (err) {
      console.error("Reset splits error:", err);
      setSplitError(err?.response?.data?.detail || "Failed to reset splits");
    }
    
    setSavingSplits(false);
  };

  const handleCancelEdit = () => {
    const originalSplits = {};
    group?.members?.forEach(m => {
      originalSplits[m.user_id] = m.split_percentage || 0;
    });
    setSplitPercentages(originalSplits);
    setEditingSplits(false);
    setSplitError("");
    setSplitSuccess("");
  };

  // ===============================================================================
  // Default Shares Helpers
  // ===============================================================================
  const handleSharesChange = (userId, value) => {
    const numValue = Math.max(0, parseInt(value) || 0);
    setDefaultShares(prev => ({
      ...prev,
      [userId]: numValue
    }));
    setSharesError("");
    setSharesSuccess("");
  };

  const handleSaveShares = async () => {
    setSavingShares(true);
    setSharesError("");
    setSharesSuccess("");
    
    try {
      const sharesArray = Object.entries(defaultShares).map(([userId, shares]) => ({
        user_id: parseInt(userId),
        shares: parseInt(shares) || 1
      }));
      
      const res = await groupsAPI.updateDefaultShares(groupId, sharesArray);
      
      if (res.data?.ok) {
        setSharesSuccess("Default shares saved successfully!");
        setEditingShares(false);
        loadGroupData();
      }
    } catch (err) {
      console.error("Save shares error:", err);
      setSharesError(err?.response?.data?.detail || "Failed to save default shares");
    }
    
    setSavingShares(false);
  };

  const handleResetShares = async () => {
    if (!window.confirm("Reset all shares to 1?")) return;
    
    setSavingShares(true);
    setSharesError("");
    setSharesSuccess("");
    
    try {
      const res = await groupsAPI.resetDefaultShares(groupId);
      
      if (res.data?.ok) {
        setSharesSuccess("Reset all shares to 1!");
        setEditingShares(false);
        loadGroupData();
      }
    } catch (err) {
      console.error("Reset shares error:", err);
      setSharesError(err?.response?.data?.detail || "Failed to reset shares");
    }
    
    setSavingShares(false);
  };

  // ===============================================================================
  // Add Expense Helpers
  // ===============================================================================
  const handleSplitTypeChange = (type) => {
    setSplitType(type);
    const amount = parseFloat(expenseAmount) || 0;
    recalculateSplits(amount, type);
  };

  const handleSplitAmountChange = (userId, value) => {
    setSplitAmounts(prev => ({
      ...prev,
      [userId]: parseFloat(value) || 0
    }));
  };

  const handleMemberSharesChange = (userId, value) => {
    const shares = Math.max(0, parseInt(value) || 0);
    setMemberShares(prev => ({
      ...prev,
      [userId]: shares
    }));
    // Recalculate when shares change
    setTimeout(() => {
      const amount = parseFloat(expenseAmount) || 0;
      const totalShares = Object.entries(memberShares).reduce((sum, [uid, s]) => {
        return sum + (uid === userId.toString() ? shares : (parseInt(s) || 0));
      }, 0);
      if (totalShares > 0) {
        const perShare = amount / totalShares;
        const newSplitAmounts = {};
        group?.members?.forEach(m => {
          const sh = m.user_id === userId ? shares : (parseInt(memberShares[m.user_id]) || 1);
          newSplitAmounts[m.user_id] = Math.round((perShare * sh) * 100) / 100;
        });
        setSplitAmounts(newSplitAmounts);
      }
    }, 0);
  };

  const handlePayerAmountChange = (userId, value) => {
    setPayerAmounts(prev => ({
      ...prev,
      [userId]: parseFloat(value) || 0
    }));
  };

  const handleEqualSplitExpense = () => {
    const amount = parseFloat(expenseAmount) || 0;
    recalculateSplits(amount, "equal");
  };

  const getSplitAmountsTotal = () => {
    return Object.values(splitAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const getPayerAmountsTotal = () => {
    return Object.values(payerAmounts).reduce((sum, val) => sum + (parseFloat(val) || 0), 0);
  };

  const getTotalShares = () => {
    return Object.values(memberShares).reduce((sum, val) => sum + (parseInt(val) || 0), 0);
  };

  // ===============================================================================
  // Add Expense
  // ===============================================================================
  const handleAddExpense = async (e) => {
    e.preventDefault();
    if (!expenseAmount || !expenseDescription) return;
    
    const amount = parseFloat(expenseAmount);
    // Validate expense amount is positive
    if (isNaN(amount) || amount <= 0) {
      alert("Expense amount must be greater than 0");
      return;
    }    
    // Validate split amounts total
    const splitTotal = getSplitAmountsTotal();
    if (Math.abs(splitTotal - amount) > 0.01) {
      alert(`Split amounts (${formatCurrency(splitTotal, currency)}) must equal expense amount (${formatCurrency(amount, currency)})`);
      return;
    }
    
    // Validate payer amounts if multiple payers
    if (multiplePayers) {
      const payerTotal = getPayerAmountsTotal();
      if (Math.abs(payerTotal - amount) > 0.01) {
        alert(`Payer amounts (${formatCurrency(payerTotal, currency)}) must equal expense amount (${formatCurrency(amount, currency)})`);
        return;
      }
    }
    
    setAddingExpense(true);
    try {
      // Build splits data
      const splits = Object.entries(splitAmounts).map(([userId, amt]) => ({
        user_id: parseInt(userId),
        amount: amt,
        shares: parseInt(memberShares[userId]) || 1
      }));
      
      // Build payers data
      let payers = null;
      if (multiplePayers) {
        payers = Object.entries(payerAmounts)
          .filter(([_, amt]) => amt > 0)
          .map(([userId, amt]) => ({
            user_id: parseInt(userId),
            amount: amt
          }));
      }
      
      const res = await expensesAPI.create(groupId, {
        amount: amount,
        description: expenseDescription,
        category: expenseCategory || null,
        split_type: splitType,
        splits: splits,
        multiple_payers: multiplePayers,
        payers: payers
      });
      
      if (res.data?.ok) {
// Reset form
setShowAddExpense(false);
setExpenseAmount("");
setExpenseDescription("");
setExpenseCategory("");
setMultiplePayers(false);
setSplitType("percentage");

// Refresh balances
loadGroupData();

// Reset expenses - will reload when user clicks Expenses tab
setExpenses([]);
setExpensesLoaded(false);
setHasMoreExpenses(true);

// Only reload if currently viewing Expenses tab
if (activeTab === "expenses") {
  loadExpenses();
}
      }
    } catch (err) {
      console.error("Add expense error:", err);
      alert(err?.response?.data?.detail || "Failed to add expense");
    }
    setAddingExpense(false);
  };

  // ===============================================================================
  // Load Expense Detail
  // ===============================================================================
  const handleViewExpense = async (expense) => {
    setLoadingExpenseDetail(true);
    setSelectedExpense(expense);
    
    try {
      const res = await expensesAPI.get(expense.id);
      if (res.data?.ok) {
        setSelectedExpense(res.data.expense);
      }
    } catch (err) {
      console.error("Load expense detail error:", err);
    }
    setLoadingExpenseDetail(false);
  };

  // ===============================================================================
  // Settle Up
  // ===============================================================================
  const handleSettleUp = async (e) => {
    e.preventDefault();
    if (!settleTo || !settleAmount) return;
    
    setSettling(true);
    try {
      const res = await settlementsAPI.create(
        groupId,
        parseInt(settleTo),
        parseFloat(settleAmount),
        settleNote
      );
      
      if (res.data?.ok) {
        setShowSettleUp(false);
        setSettleTo("");
        setSettleAmount("");
        setSettleNote("");
        loadGroupData();
      }
    } catch (err) {
      console.error("Settlement error:", err);
      alert(err?.response?.data?.detail || "Failed to record settlement");
    }
    setSettling(false);
  };

  // ===============================================================================
  // Delete Expense
  // ===============================================================================

  const handleDeleteExpense = async (expenseId) => {
  if (!window.confirm("Are you sure you want to delete this expense?")) return;
  
  try {
    const res = await expensesAPI.delete(expenseId);
    if (res.data?.ok) {
      // Remove from local state immediately
      setExpenses(prev => prev.filter(e => e.id !== expenseId));
      // Refresh balances
      loadGroupData();
    }
  } catch (err) {
    console.error("Delete expense error:", err);
    alert(err?.response?.data?.detail || "Failed to delete expense");
  }
};

  // ===============================================================================
  // Leave/Delete Group
  // ===============================================================================
  const handleLeaveGroup = async () => {
    if (!window.confirm("Are you sure you want to leave this group?")) return;
    
    try {
      await groupsAPI.leave(groupId);
      onBack();
    } catch (err) {
      console.error("Leave group error:", err);
      alert(err?.response?.data?.detail || "Failed to leave group");
    }
  };

  const handleDeleteGroup = async () => {
    if (!window.confirm("Are you sure you want to DELETE this group? This cannot be undone!")) return;
    
    try {
      await groupsAPI.delete(groupId);
      onGroupDeleted();
    } catch (err) {
      console.error("Delete group error:", err);
      alert(err?.response?.data?.detail || "Failed to delete group");
    }
  };

  // ===============================================================================
  // Copy Invite Code
  // ===============================================================================
  const copyInviteCode = () => {
    const code = group?.invite_code || "";
    
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code);
      alert("Invite code copied!");
    } else {
      const textArea = document.createElement("textarea");
      textArea.value = code;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        alert("Invite code copied!");
      } catch (err) {
        alert("Copy failed. Code: " + code);
      }
      document.body.removeChild(textArea);
    }
  };

  // ===============================================================================
  // Export Functions
  // ===============================================================================
  const handleExportExpenses = async () => {
    try {
      const response = await exportAPI.expensesCsv(groupId);
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${group?.name || 'expenses'}_expenses.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export expenses error:", err);
      alert("Failed to export expenses");
    }
  };

  const handleExportSettlements = async () => {
    try {
      const response = await exportAPI.settlementsCsv(groupId);
      const contentDisposition = response.headers['content-disposition'];
      let filename = `${group?.name || 'settlements'}_settlements.csv`;
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (filenameMatch) filename = filenameMatch[1];
      }
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Export settlements error:", err);
      alert("Failed to export settlements");
    }
  };

  // ===============================================================================
  // Receipt Functions (Premium Feature)
  // ===============================================================================
  
   const handleReceiptFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("File too large. Maximum size: 5 MB");
        e.target.value = "";
        return;
      }
      // Validate file type
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "application/pdf"];
      if (!allowedTypes.includes(file.type)) {
        alert("Invalid file type. Allowed: JPG, PNG, PDF");
        e.target.value = "";
        return;
      }
      setReceiptFile(file);
    }
  };

  const handleUploadReceipt = async (expenseId) => {
    if (!receiptFile) return;
    
    setUploadingReceipt(true);
    try {
      const response = await receiptsAPI.upload(expenseId, receiptFile);
      if (response.data?.ok) {
        alert("Receipt uploaded successfully!");
        setReceiptFile(null);
        // Reload receipt info
        loadReceiptInfo(expenseId);
      }
    } catch (err) {
      console.error("Upload receipt error:", err);
      const message = err.response?.data?.detail || "Failed to upload receipt";
      alert(message);
    }
    setUploadingReceipt(false);
  };

  const loadReceiptInfo = async (expenseId) => {
    setLoadingReceipt(true);
    try {
      const response = await receiptsAPI.getInfo(expenseId);
      if (response.data?.ok) {
        setReceiptInfo(response.data);
      }
    } catch (err) {
      console.error("Load receipt info error:", err);
      setReceiptInfo(null);
    }
    setLoadingReceipt(false);
  };

  const handleDownloadReceipt = async (expenseId, filename) => {
    try {
      const response = await receiptsAPI.download(expenseId);
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error("Download receipt error:", err);
      alert("Failed to download receipt");
    }
  };

  const handleDeleteReceipt = async (expenseId) => {
    if (!window.confirm("Are you sure you want to delete this receipt?")) return;
    
    try {
      const response = await receiptsAPI.delete(expenseId);
      if (response.data?.ok) {
        alert("Receipt deleted successfully!");
        setReceiptInfo(null);
      }
    } catch (err) {
      console.error("Delete receipt error:", err);
      const message = err.response?.data?.detail || "Failed to delete receipt";
      alert(message);
    }
  };

  // Load receipt info when expense detail modal opens
  useEffect(() => {
    if (selectedExpense && selectedExpense.id) {
      loadReceiptInfo(selectedExpense.id);
    } else {
      setReceiptInfo(null);
    }
  }, [selectedExpense]);

  // ===============================================================================
  // Render Loading
  // ===============================================================================
  if (loading) {
    return (
      <div className="group-detail">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>Loading group...</p>
        </div>
      </div>
    );
  }

  if (error || !group) {
    return (
      <div className="group-detail">
        <div className="error-container">
          <p>{error || "Group not found"}</p>
          <button onClick={onBack}>Go Back</button>
        </div>
      </div>
    );
  }

  const isAdmin = group.my_role === "admin";
  const currency = group.currency || "EUR";
  const splitTotal = calculateSplitTotal();
  const isSplitValid = Math.abs(splitTotal - 100) <= 0.01;
  const expenseAmountNum = parseFloat(expenseAmount) || 0;
  const splitAmountsTotal = getSplitAmountsTotal();
  const payerAmountsTotal = getPayerAmountsTotal();

  // ===============================================================================
  // Render
  // ===============================================================================
  return (
    <div className="group-detail">
      {/* Header */}
      <div className="group-header">
       {/* <button className="back-btn" onClick={onBack}>← Back</button> */}
        <div className="group-header-info">
         {/* <div className="group-icon">{group.name.charAt(0).toUpperCase()}</div> */}
          <div>
            <h1>{group.name}</h1>
            <p style={{ fontWeight: 600, color: '#960a08', margin: 0, fontSize: '13px' }}>{group.members?.length || 0} members • {currency}</p>
          </div>
        </div>
       {/* <div className="group-header-actions">
          <span className="currency-badge">{currency}</span>
        </div> */}
      </div>
 
        <div className="group-actions">
          <button className="btn btn-primary" onClick={() => setShowAddExpense(true)}>
            ➕ Add Expense
          </button>
          <button className="btn btn-secondary btn-sm" onClick={loadGroupData}>
            🔄 Refresh Data
          </button>
          <button className="btn btn-secondary" onClick={() => setShowInvite(true)}>
           🙋‍♂️  Invite
          </button>
          <button className="btn btn-secondary" onClick={handleExportExpenses}>
            📥 Export Expenses
          </button>
          <button className="btn btn-secondary" onClick={handleExportSettlements}>
            📥 Export Settlements
          </button>
        </div>

      {/* Tabs */}
      <div className="tabs">
        <button 
          className={`tab ${activeTab === "balances" ? "active" : ""}`}
          onClick={() => setActiveTab("balances")}
        >
          Balances
        </button>
        <button 
          className={`tab ${activeTab === "members" ? "active" : ""}`}
          onClick={() => setActiveTab("members")}
        >
          Members
        </button>
        <button 
          className={`tab ${activeTab === "settings" ? "active" : ""}`}
          onClick={() => setActiveTab("settings")}
        >
          Settings
        </button>
        <button
          className={`tab ${activeTab === "expenses" ? "active" : ""}`}
          onClick={() => {
            setActiveTab("expenses");
            loadExpenses();
          }}
         >
            Expenses ({expenses.length})
        </button>
      </div>

{/* Expenses Tab */}
{activeTab === "expenses" && (
  <div className="tab-content">
    {loadingExpenses && expenses.length === 0 ? (
      <div className="loading-container">
        <div className="spinner"></div>
        <p>Loading expenses...</p>
      </div>
    ) : expenses.length === 0 ? (
      <div className="empty-state">
        <div className="empty-icon">📝</div>
        <h3>No Expenses Yet</h3>
        <p>Add your first expense to start tracking</p>
        <button className="btn btn-primary" onClick={() => setShowAddExpense(true)}>
          ➕ Add Expense
        </button>
      </div>
    ) : (
      <>
        <div className="expenses-list">
          {expenses.map((expense) => (
            <div 
              key={expense.id} 
              className="expense-item clickable"
              onClick={() => handleViewExpense(expense)}
            >
              <div className="expense-main">
                <div className="expense-info">
                  <span className="expense-desc">{expense.description}</span>
                  <span className="expense-meta">
                    {expense.multiple_payers ? "Multiple payers" : `Paid by ${expense.paid_by_name}`} • {formatDate(expense.expense_date)}
                    {expense.category && ` • ${expense.category}`}
                  </span>
                </div>
               <div className="expense-amount">
                {expense.has_receipt && <span className="receipt-indicator" title="Has receipt">🧾</span>}
                {formatCurrency(expense.amount, currency)}
               </div>
             </div>
             {(expense.paid_by === user?.id || isAdmin) && (
                <button 
                  className="delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteExpense(expense.id);
                  }}
                >
                  🗑️
                </button>
              )}
            </div>
          ))}
        </div>
        
        {/* Load More Button */}
        {hasMoreExpenses && (
          <button 
            className="btn btn-secondary"
            onClick={() => loadExpenses(true)}
            disabled={loadingExpenses}
            style={{ marginTop: '16px', width: '100%' }}
          >
            {loadingExpenses ? "Loading..." : "Load More Expenses"}
          </button>
        )}
      </>
    )}
  </div>
)}

      {/* Balances Tab */}
      {activeTab === "balances" && balances && (
        <div className="tab-content">
          <div className="balances-section">
            <h3>Member Balances</h3>
            <div className="balances-list">
              {balances.balances?.map((b) => (
                <div key={b.user_id} className="balance-row">
                  <div className="balance-user">
                    <div className="balance-avatar">{b.name.charAt(0)}</div>
                    <span className="balance-name">{b.name}</span>
                  </div>
                  <span className={`balance-amount ${b.net_balance >= 0 ? 'positive' : 'negative'}`}>
                    {b.net_balance >= 0 ? '+' : ''}{formatCurrency(b.net_balance, currency)}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {balances.debts?.length > 0 && (
            <div className="debts-section">
              <h3>Who Pays Who</h3>
              <div className="debts-list">
                {balances.debts.map((debt, i) => (
                  <div key={i} className="debt-item">
                    <span className="debt-from">{debt.from_user_name}</span>
                    <span className="debt-arrow">→</span>
                    <span className="debt-to">{debt.to_user_name}</span>
                    <span className="debt-amount">{formatCurrency(debt.amount, currency)}</span>
                    {debt.from_user_id === user?.id && (
                      <button 
                        className="debt-settle-btn"
                        onClick={() => {
                          setSettleTo(debt.to_user_id.toString());
                          setSettleAmount(debt.amount.toString());
                          setShowSettleUp(true);
                        }}
                      >
                        Settle
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {balances.debts?.length === 0 && (
            <div className="all-settled">
              <div className="settled-icon">✅</div>
              <h3>All Settled!</h3>
              <p>Everyone is even. No payments needed.</p>
            </div>
          )}

          <button className="btn btn-secondary settle-btn" onClick={() => setShowSettleUp(true)}>
            💸 Record Settlement
          </button>

         {/* Settlement History - Lazy Loaded */}
          <div className="settlements-section">
            <h3>Payment History</h3>
            {!settlementsLoaded ? (
              <div className="load-history-container">
                <button 
                  className="btn btn-secondary"
                  onClick={loadSettlements}
                  disabled={loadingSettlements}
                >
                  {loadingSettlements ? (
                    <>
                      <span className="spinner-small"></span>
                      Loading...
                    </>
                  ) : (
                    "📜 Load Payment History"
                  )}
                </button>
              </div>
            ) : settlements.length === 0 ? (
              <div className="empty-settlements">
                <p>No payments recorded yet</p>
              </div>
            ) : (
              <div className="settlements-list">
                {settlements.map((s) => (
                  <div key={s.id} className="settlement-item">
                    <div className="settlement-info">
                      <span className="settlement-from">{s.from_user_name}</span>
                      <span className="settlement-arrow">paid</span>
                      <span className="settlement-to">{s.to_user_name}</span>
                    </div>
                    <div className="settlement-details">
                      <span className="settlement-amount">{formatCurrency(s.amount, currency)}</span>
                      <span className="settlement-date">{formatDate(s.created_at)}</span>
                      {s.note && <span className="settlement-note">({s.note})</span>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Members Tab */}
      {activeTab === "members" && (
        <div className="tab-content">
          {/* Split Percentages Section */}
          <div className="split-percentages-section">
            <div className="split-header">
              <h3>📊 Default Split Percentages (only Group-Admin has access)</h3>
              {isAdmin && !editingSplits && (
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingSplits(true)}>
                  ✏️ Edit
                </button>
              )}
            </div>
            
            <p className="split-description">
              {group.has_custom_splits 
                ? "Using custom split percentages for expenses"
                : "Using equal splits for all members"
              }
            </p>

            {splitError && <div className="split-error">{splitError}</div>}
            {splitSuccess && <div className="split-success">{splitSuccess}</div>}

            <div className="members-split-list">
              {group.members?.map((member) => (
                <div key={member.id} className="member-split-item">
                  <div className="member-split-left">
                    <div className="member-avatar-sm">{member.name.charAt(0)}</div>
                    <div className="member-info-sm">
                      <span className="member-name-sm">
                        {member.name}
                        {member.user_id === user?.id && <span className="you-badge-sm">You</span>}
                      </span>
                    </div>
                  </div>
                  
                  <div className="member-split-right">
                    {editingSplits ? (
                      <div className="split-input-group">
                        <input
                          type="number"
                          className="split-input"
                          value={splitPercentages[member.user_id] ?? ""}
                          onChange={(e) => handleSplitChange(member.user_id, e.target.value)}
                          min="0"
                          max="100"
                          step="0.01"
                        />
                        <span className="split-percent-sign">%</span>
                      </div>
                    ) : (
                      <div className="split-display">
                        <span className="split-value">{member.split_percentage?.toFixed(1) || "0.0"}%</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {editingSplits && (
              <div className="split-edit-controls">
                <div className={`split-total ${isSplitValid ? 'valid' : 'invalid'}`}>
                  Total: <strong>{splitTotal.toFixed(1)}%</strong>
                  {!isSplitValid && <span className="total-hint"> (must be 100%)</span>}
                </div>
                
                <div className="split-buttons">
                  <button className="btn btn-secondary btn-sm" onClick={handleEqualSplit} disabled={savingSplits}>
                    ⚖️ Equal
                  </button>
                  <button className="btn btn-secondary btn-sm" onClick={handleCancelEdit} disabled={savingSplits}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveSplits} disabled={savingSplits || !isSplitValid}>
                    {savingSplits ? "Saving..." : "💾 Save"}
                  </button>
                </div>
                
                {group.has_custom_splits && (
                  <button className="btn btn-link" onClick={handleResetSplits} disabled={savingSplits}>
                    🔄 Reset to Equal
                  </button>
                )}
              </div>
            )}
          </div>

          {/* Default Shares Section */}
          <div className="split-percentages-section">
            <div className="split-header">
              <h3>👥 Default Shares (Guests)</h3>
              {isAdmin && !editingShares && (
                <button className="btn btn-secondary btn-sm" onClick={() => setEditingShares(true)}>
                  ✏️ Edit
                </button>
              )}
            </div>
            
            <p className="split-description">
              Set default guest count per member (member + guests = shares)
            </p>

            {sharesError && <div className="split-error">{sharesError}</div>}
            {sharesSuccess && <div className="split-success">{sharesSuccess}</div>}

            <div className="members-split-list">
              {group.members?.map((member) => (
                <div key={member.id} className="member-split-item">
                  <div className="member-split-left">
                    <div className="member-avatar-sm">{member.name.charAt(0)}</div>
                    <div className="member-info-sm">
                      <span className="member-name-sm">{member.name}</span>
                    </div>
                  </div>
                  
                  <div className="member-split-right">
                    {editingShares ? (
                      <div className="shares-input-group">
                        <input
                          type="number"
                          className="shares-input"
                          value={defaultShares[member.user_id] ?? 1}
                          onChange={(e) => handleSharesChange(member.user_id, e.target.value)}
                          min="0"
                        />
                        <span className="shares-label">shares</span>
                      </div>
                    ) : (
                      <div className="split-display">
                        <span className="split-value">{member.default_shares || 1} {(member.default_shares || 1) === 1 ? 'share' : 'shares'}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {editingShares && (
              <div className="split-edit-controls">
                <div className="split-buttons">
                  <button className="btn btn-secondary btn-sm" onClick={() => setEditingShares(false)} disabled={savingShares}>
                    Cancel
                  </button>
                  <button className="btn btn-primary btn-sm" onClick={handleSaveShares} disabled={savingShares}>
                    {savingShares ? "Saving..." : "💾 Save"}
                  </button>
                </div>
                
                {group.has_custom_shares && (
                  <button className="btn btn-link" onClick={handleResetShares} disabled={savingShares}>
                    🔄 Reset to 1
                  </button>
                )}
              </div>
            )}
          </div>

          <div className="members-divider"></div>

          {/* All Members List */}
          <h3>👥 All Members</h3>
          <div className="members-list">
            {group.members?.map((member) => (
              <div key={member.id} className="member-item">
                <div className="member-avatar-sm">{member.name.charAt(0)}</div>
                <div className="member-info-sm">
                  <span className="member-name-sm">{member.name}</span>
                  <span className="member-role-sm">{member.role}</span>
                </div>
                {member.user_id === user?.id && <span className="you-badge-sm">You</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Settings Tab */}
      {activeTab === "settings" && (
        <div className="tab-content">
          <div className="settings-section">
            <h3>Group Info</h3>
            <div className="setting-item">
              <span>Name:</span>
              <strong>{group.name}</strong>
            </div>
            <div className="setting-item">
              <span>Currency:</span>
              <strong>{group.currency}</strong>
            </div>
            <div className="setting-item">
              <span>Invite Code:</span>
              <strong className="invite-code">{group.invite_code}</strong>
              <button onClick={copyInviteCode}>Copy</button>
            </div>
          </div>

          <div className="settings-section danger">
            <h3>Danger Zone</h3>
            <button className="btn btn-danger" onClick={handleLeaveGroup}>
              🚪 Leave Group
            </button>
            {isAdmin && (
              <button className="btn btn-danger" onClick={handleDeleteGroup}>
                🗑️ Delete Group
              </button>
            )}
          </div>
        </div>
      )}

      {/* Add Expense Modal */}
      {showAddExpense && (
        <div className="modal-overlay" onClick={() => setShowAddExpense(false)}>
          <div className="modal modal-wide" onClick={(e) => e.stopPropagation()}>
            <h2>Add Expense</h2>
            <form onSubmit={handleAddExpense}>
              <div className="form-row">
                <div className="form-group">
                  <label>Amount ({currency}) *</label>
                  <input
                    type="number"
                    step="0.01"
                    value={expenseAmount}
                    onChange={(e) => setExpenseAmount(e.target.value)}
                    placeholder="0.00"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <select value={expenseCategory} onChange={(e) => setExpenseCategory(e.target.value)}>
                    {categories.map((c) => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              
              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={expenseDescription}
                  onChange={(e) => setExpenseDescription(e.target.value)}
                  placeholder="What was this for?"
                  required
                />
              </div>

              {/* Multiple Payers Checkbox */}
              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={multiplePayers}
                    onChange={(e) => setMultiplePayers(e.target.checked)}
                  />
                  <span>Multiple Payers</span>
                </label>
              </div>

              {/* Payers Section */}
              {multiplePayers && (
                <div className="split-options-section">
                  <h4>💳 Who Paid?</h4>
                  <div className="split-members-list">
                    {group.members?.map((member) => (
                      <div key={member.user_id} className="split-member-row">
                        <span className="split-member-name">{member.name}</span>
                        <div className="split-member-input">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={payerAmounts[member.user_id] || ""}
                            onChange={(e) => handlePayerAmountChange(member.user_id, e.target.value)}
                            placeholder="0.00"
                          />
                          <span className="currency-label">{currency}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className={`split-total-row ${Math.abs(payerAmountsTotal - expenseAmountNum) <= 0.01 ? 'valid' : 'invalid'}`}>
                    Total: {formatCurrency(payerAmountsTotal, currency)}
                    {Math.abs(payerAmountsTotal - expenseAmountNum) > 0.01 && (
                      <span className="total-diff"> (need {formatCurrency(expenseAmountNum, currency)})</span>
                    )}
                  </div>
                </div>
              )}

              {/* Split Options Section - Always Visible */}
              <div className="split-options-section">
                <div className="split-options-header">
                  <h4>📊 Split Options</h4>
                  <div className="split-type-tabs">
                    <button
                      type="button"
                      className={`split-tab ${splitType === "percentage" ? "active" : ""}`}
                      onClick={() => handleSplitTypeChange("percentage")}
                    >
                      % Default
                    </button>
                    <button
                      type="button"
                      className={`split-tab ${splitType === "shares" ? "active" : ""}`}
                      onClick={() => handleSplitTypeChange("shares")}
                    >
                      👥 Shares
                    </button>
                  </div>
                </div>

                {/* Shares Input (when shares type selected) */}
                {splitType === "shares" && (
                  <div className="shares-section">
                    <p className="shares-hint">Enter total shares per member (member + guests)</p>
                    <div className="split-members-list">
                      {group.members?.map((member) => (
                        <div key={member.user_id} className="split-member-row shares-row">
                          <span className="split-member-name">{member.name}</span>
                          <div className="shares-input-wrapper">
                            <input
                              type="number"
                              min="0"
                              value={memberShares[member.user_id] || 1}
                              onChange={(e) => handleMemberSharesChange(member.user_id, e.target.value)}
                              className="shares-num-input"
                            />
                            <span className="shares-text">shares</span>
                          </div>
                          <span className="split-amount-display">
                            = {formatCurrency(splitAmounts[member.user_id] || 0, currency)}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="shares-total">
                      Total: {getTotalShares()} shares
                    </div>
                  </div>
                )}

                {/* Split Amounts (for percentage/equal) */}
                {splitType !== "shares" && (
                  <div className="split-members-list">
                    {group.members?.map((member) => (
                      <div key={member.user_id} className="split-member-row">
                        <span className="split-member-name">
                          {member.name}
                          {splitType === "percentage" && (
                            <span className="pct-hint">({member.split_percentage?.toFixed(0) || 0}%)</span>
                          )}
                        </span>
                        <div className="split-member-input">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={splitAmounts[member.user_id] || ""}
                            onChange={(e) => handleSplitAmountChange(member.user_id, e.target.value)}
                            placeholder="0.00"
                          />
                          <span className="currency-label">{currency}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                <div className={`split-total-row ${Math.abs(splitAmountsTotal - expenseAmountNum) <= 0.01 ? 'valid' : 'invalid'}`}>
                  Split Total: {formatCurrency(splitAmountsTotal, currency)}
                  {Math.abs(splitAmountsTotal - expenseAmountNum) > 0.01 && (
                    <span className="total-diff"> (need {formatCurrency(expenseAmountNum, currency)})</span>
                  )}
                </div>

                {splitType !== "equal" && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm equal-btn"
                    onClick={handleEqualSplitExpense}
                  >
                    ⚖️ Split Equally
                  </button>
                )}
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowAddExpense(false)}>Cancel</button>
                <button 
                  type="submit" 
                  className="btn btn-primary" 
                  disabled={addingExpense || Math.abs(splitAmountsTotal - expenseAmountNum) > 0.01}
                >
                  {addingExpense ? "Adding..." : "Add Expense"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Expense Detail Modal */}
      {selectedExpense && (
        <div className="modal-overlay" onClick={() => setSelectedExpense(null)}>
          <div className="modal modal-detail" onClick={(e) => e.stopPropagation()}>
            <h2>Expense Details</h2>
            
            {loadingExpenseDetail ? (
              <div className="loading-spinner-small">Loading...</div>
            ) : (
              <div className="expense-detail-content">
                <div className="expense-detail-header">
                  <div className="expense-detail-title">
                    <span className="expense-category-icon">
                      {categories.find(c => c.value === selectedExpense.category)?.label.split(' ')[0] || '📦'}
                    </span>
                    <div>
                      <h3>{selectedExpense.description}</h3>
                      <p className="expense-detail-meta">
                        {formatDate(selectedExpense.expense_date)} • {selectedExpense.category || 'Other'}
                      </p>
                    </div>
                  </div>
                  <div className="expense-detail-amount">
                    {formatCurrency(selectedExpense.amount, currency)}
                  </div>
                </div>

                <div className="expense-detail-section">
                  <h4>💳 Paid By</h4>
                  <div className="detail-list">
                    {selectedExpense.payers?.length > 0 ? (
                      selectedExpense.payers.map((p, i) => (
                        <div key={i} className="detail-row">
                          <span>{p.name}</span>
                          <span className="detail-amount positive">{formatCurrency(p.amount, currency)}</span>
                        </div>
                      ))
                    ) : (
                      <div className="detail-row">
                        <span>{selectedExpense.paid_by_name}</span>
                        <span className="detail-amount positive">{formatCurrency(selectedExpense.amount, currency)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="expense-detail-section">
                  <h4>📊 Split Between</h4>
                  <div className="detail-list">
                    {selectedExpense.splits?.map((s, i) => (
                      <div key={i} className="detail-row">
                        <span>
                          {s.name}
                          <span className="shares-badge">({(s.split_percentage || 0).toFixed(1)}%)</span>
                        </span>
                        <span className="detail-amount">{formatCurrency(s.amount, currency)}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="expense-detail-section">
                  <h4>📈 Net Effect</h4>
                  <div className="detail-list">
                    {selectedExpense.splits?.map((s, i) => {
                      // Find how much this person paid
                      let paid = 0;
                      if (selectedExpense.payers?.length > 0) {
                        const payer = selectedExpense.payers.find(p => p.user_id === s.user_id);
                        paid = payer?.amount || 0;
                      } else if (selectedExpense.paid_by === s.user_id) {
                        paid = selectedExpense.amount;
                      }
                      const net = paid - s.amount;
                      return (
                        <div key={i} className="detail-row">
                          <span>{s.name}</span>
                          <span className={`detail-amount ${net >= 0 ? 'positive' : 'negative'}`}>
                            {net >= 0 ? '+' : ''}{formatCurrency(net, currency)}
                          </span>
                        </div>
                      );
                    })}
</div>
                </div>

                {/* Receipt Section */}
                <div className="expense-detail-section">
                  <h4>🧾 Receipt</h4>
                  {loadingReceipt ? (
                    <div className="loading-spinner-small">Loading...</div>
                  ) : receiptInfo?.has_receipt ? (
                    <div className="receipt-section">
                      <div className="receipt-info">
                        <span className="receipt-filename">{receiptInfo.receipt.original_filename}</span>
                        <span className="receipt-size">
                          ({(receiptInfo.receipt.file_size / 1024).toFixed(1)} KB)
                        </span>
                      </div>
                      <div className="receipt-actions">
                        <button 
                          className="btn btn-secondary btn-sm"
                          onClick={() => handleDownloadReceipt(selectedExpense.id, receiptInfo.receipt.original_filename)}
                        >
                          📥 Download
                        </button>
                        {selectedExpense?.created_by === user?.id && (
                          <button 
                            className="btn btn-danger btn-sm"
                            onClick={() => handleDeleteReceipt(selectedExpense.id)}
                          >
                            🗑️ Delete
                          </button>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="receipt-section">
                      {user?.is_premium ? (
                        selectedExpense?.created_by === user?.id ? (
                          <div className="receipt-upload">
                            <input
                              type="file"
                              accept=".jpg,.jpeg,.png,.pdf"
                              onChange={handleReceiptFileChange}
                              id="receipt-file-input"
                              className="file-input"
                            />
                            <label htmlFor="receipt-file-input" className="file-label">
                              {receiptFile ? receiptFile.name : "Choose file (JPG, PNG, PDF - Max 5MB)"}
                            </label>
                            {receiptFile && (
                              <button
                                className="btn btn-primary btn-sm"
                                onClick={() => handleUploadReceipt(selectedExpense.id)}
                                disabled={uploadingReceipt}
                              >
                                {uploadingReceipt ? "Uploading..." : "📤 Upload"}
                              </button>
                            )}
                          </div>
                        ) : (
                          <p className="no-receipt-text">No receipt attached</p>
                        )
                      ) : (
                        <p className="premium-required-text">
                          ⭐ Premium feature - Upgrade to attach receipts
                        </p>
                      )}
                    </div>
                  )}
                </div>

              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setSelectedExpense(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Settle Up Modal */}
      {showSettleUp && (
        <div className="modal-overlay" onClick={() => setShowSettleUp(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Record Settlement</h2>
            <form onSubmit={handleSettleUp}>
              <div className="form-group">
                <label>Pay to *</label>
                <select value={settleTo} onChange={(e) => setSettleTo(e.target.value)} required>
                  <option value="">Select person...</option>
                  {group.members?.filter(m => m.user_id !== user?.id).map((m) => (
                    <option key={m.user_id} value={m.user_id}>{m.name}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Amount ({currency}) *</label>
                <input
                  type="number"
                  step="0.01"
                  value={settleAmount}
                  onChange={(e) => setSettleAmount(e.target.value)}
                  placeholder="0.00"
                  required
                />
              </div>
              <div className="form-group">
                <label>Note (optional)</label>
                <input
                  type="text"
                  value={settleNote}
                  onChange={(e) => setSettleNote(e.target.value)}
                  placeholder="e.g., PayPal, Cash"
                />
              </div>
              <div className="modal-actions">
                <button type="button" onClick={() => setShowSettleUp(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={settling}>
                  {settling ? "Recording..." : "Record Payment"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Invite Modal */}
      {showInvite && (
        <div className="modal-overlay" onClick={() => setShowInvite(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>Invite Friends</h2>
            <p>Share this code with friends to join your group:</p>
            <div className="invite-code-display">
              {group.invite_code}
            </div>
            <div className="modal-actions">
              <button onClick={copyInviteCode}>📋 Copy Code</button>
              <button onClick={() => setShowInvite(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default GroupDetail;

